import type { LocationExactnessScope } from "./constants";
import { LOCATION_EXACTNESS_SCOPE } from "./constants";
import type { PlaceLevel } from "./types";
import { PLACE_LEVEL } from "./types";

export interface PlaceExactnessOption {
    id: string;
    labelKey: string;
    fullLabelKey: string;
    value: LocationExactnessScope;
    adjacent: boolean;
    level: PlaceLevel;
}

/**
 * Determines if exactness options for a specific place level should be excluded
 * based on the location's ID hierarchy.
 *
 * - County-level options are excluded for Australia (5027) and Canada (3243)
 * - State-level options are excluded for all countries under United Kingdom (3257)
 */
const shouldExcludeOption = (optionLevel: PlaceLevel, idHierarchy: number[]): boolean => {
    const COUNTY_BLACKLIST = [5027, 3243]; // Australia, Canada
    const STATE_BLACKLIST = [3257]; // United Kingdom

    if (optionLevel === PLACE_LEVEL.County && COUNTY_BLACKLIST.some((id) => idHierarchy.includes(id))) {
        return true;
    }
    if (optionLevel === PLACE_LEVEL.State && STATE_BLACKLIST.some((id) => idHierarchy.includes(id))) {
        return true;
    }
    return false;
};

export const getPlaceExactnessOptions = (level: PlaceLevel, idHierarchy: number[] = []): PlaceExactnessOption[] => {
    let options: PlaceExactnessOption[] = [];
    switch (level) {
        case PLACE_LEVEL.Continent:
        case PLACE_LEVEL.CountryRegion:
        case PLACE_LEVEL.Country:
        case PLACE_LEVEL.StateRegion:
            break;
        case PLACE_LEVEL.State:
        case PLACE_LEVEL.CountyRegion:
            if (shouldExcludeOption(PLACE_LEVEL.State, idHierarchy)) break;
            options = [
                {
                    id: LOCATION_EXACTNESS_SCOPE.ThisPlace,
                    value: LOCATION_EXACTNESS_SCOPE.ThisPlace,
                    labelKey: "location.exactness.thisPlace",
                    fullLabelKey: "location.exactness.exactToThisPlace",
                    adjacent: false,
                    level: PLACE_LEVEL.State,
                },
                {
                    id: LOCATION_EXACTNESS_SCOPE.ThisPlaceAdjacent,
                    value: LOCATION_EXACTNESS_SCOPE.ThisPlaceAdjacent,
                    labelKey: "location.exactness.stateAndAdjacentStates",
                    fullLabelKey: "location.exactness.exactToStateAndAdjacentStates",
                    adjacent: true,
                    level: PLACE_LEVEL.State,
                },
                {
                    id: LOCATION_EXACTNESS_SCOPE.OneLevelUp,
                    value: LOCATION_EXACTNESS_SCOPE.OneLevelUp,
                    labelKey: "location.exactness.country",
                    fullLabelKey: "location.exactness.exactToCountry",
                    adjacent: false,
                    level: PLACE_LEVEL.Country,
                },
            ];
            break;
        case PLACE_LEVEL.County:
            if (shouldExcludeOption(PLACE_LEVEL.County, idHierarchy)) break;
            options = [
                {
                    id: LOCATION_EXACTNESS_SCOPE.ThisPlace,
                    value: LOCATION_EXACTNESS_SCOPE.ThisPlace,
                    labelKey: "location.exactness.thisPlace",
                    fullLabelKey: "location.exactness.exactToThisPlace",
                    adjacent: false,
                    level: PLACE_LEVEL.County,
                },
                {
                    id: LOCATION_EXACTNESS_SCOPE.ThisPlaceAdjacent,
                    value: LOCATION_EXACTNESS_SCOPE.ThisPlaceAdjacent,
                    labelKey: "location.exactness.countyAndAdjacentCounties",
                    fullLabelKey: "location.exactness.exactToCountyAndAdjacentCounties",
                    adjacent: true,
                    level: PLACE_LEVEL.County,
                },
                {
                    id: LOCATION_EXACTNESS_SCOPE.OneLevelUp,
                    value: LOCATION_EXACTNESS_SCOPE.OneLevelUp,
                    labelKey: "location.exactness.state",
                    fullLabelKey: "location.exactness.exactToState",
                    adjacent: false,
                    level: PLACE_LEVEL.State,
                },
                {
                    id: LOCATION_EXACTNESS_SCOPE.OneLevelUpAdjacent,
                    value: LOCATION_EXACTNESS_SCOPE.OneLevelUpAdjacent,
                    labelKey: "location.exactness.stateAndAdjacentStates",
                    fullLabelKey: "location.exactness.exactToStateAndAdjacentStates",
                    adjacent: true,
                    level: PLACE_LEVEL.State,
                },
                {
                    id: LOCATION_EXACTNESS_SCOPE.TwoLevelsUp,
                    value: LOCATION_EXACTNESS_SCOPE.TwoLevelsUp,
                    labelKey: "location.exactness.country",
                    fullLabelKey: "location.exactness.exactToCountry",
                    adjacent: false,
                    level: PLACE_LEVEL.Country,
                },
            ];
            break;
        case PLACE_LEVEL.City:
        case PLACE_LEVEL.Township:
        case PLACE_LEVEL.PostalCode:
            options = [
                {
                    id: LOCATION_EXACTNESS_SCOPE.ThisPlace,
                    value: LOCATION_EXACTNESS_SCOPE.ThisPlace,
                    labelKey: "location.exactness.thisPlace",
                    fullLabelKey: "location.exactness.exactToThisPlace",
                    adjacent: false,
                    level,
                },
                {
                    id: LOCATION_EXACTNESS_SCOPE.OneLevelUp,
                    value: LOCATION_EXACTNESS_SCOPE.OneLevelUp,
                    labelKey: "location.exactness.county",
                    fullLabelKey: "location.exactness.exactToCounty",
                    adjacent: false,
                    level: PLACE_LEVEL.County,
                },
                {
                    id: LOCATION_EXACTNESS_SCOPE.OneLevelUpAdjacent,
                    value: LOCATION_EXACTNESS_SCOPE.OneLevelUpAdjacent,
                    labelKey: "location.exactness.countyAndAdjacentCounties",
                    fullLabelKey: "location.exactness.exactToCountyAndAdjacentCounties",
                    adjacent: true,
                    level: PLACE_LEVEL.County,
                },
                {
                    id: LOCATION_EXACTNESS_SCOPE.TwoLevelsUp,
                    value: LOCATION_EXACTNESS_SCOPE.TwoLevelsUp,
                    labelKey: "location.exactness.state",
                    fullLabelKey: "location.exactness.exactToState",
                    adjacent: false,
                    level: PLACE_LEVEL.State,
                },
                {
                    id: LOCATION_EXACTNESS_SCOPE.TwoLevelsUpAdjacent,
                    value: LOCATION_EXACTNESS_SCOPE.TwoLevelsUpAdjacent,
                    labelKey: "location.exactness.stateAndAdjacentStates",
                    fullLabelKey: "location.exactness.exactToStateAndAdjacentStates",
                    adjacent: true,
                    level: PLACE_LEVEL.State,
                },
                {
                    id: LOCATION_EXACTNESS_SCOPE.ThreeLevelsUp,
                    value: LOCATION_EXACTNESS_SCOPE.ThreeLevelsUp,
                    labelKey: "location.exactness.country",
                    fullLabelKey: "location.exactness.exactToCountry",
                    adjacent: false,
                    level: PLACE_LEVEL.Country,
                },
            ];
            break;
        default:
            throw new Error(`Unsupported place level: ${level}`);
    }

    return options.filter((opt) => !shouldExcludeOption(opt.level, idHierarchy));
};
